import type { MarketVariants } from '@/lib/i18n'

export type ReportLink = {
  text: string
  href: string
  /** Picks the link styling: web-style (wraps anywhere) or number-style (tabular). */
  kind: 'url' | 'phone'
}

export type ReportChannel = {
  org: string
  /** Plain fallback shown when `links` is empty. */
  detail: string
  whenToUse: string
  links: ReportLink[]
}

export type Slide7ReportCopy = {
  eyebrow: string
  title: string
  body: string
  startHereLabel: string
  buttonLabel: string
  closingLine: string
  introLine: string
  haveTheseHeading: string
  have1: string
  have2: string
  have3: string
  have4: string
  whereToReportPrefix: string
  policeNote: string
  whatHappensNextHeading: string
  whatHappensNextBody: string
  documentationLine: string
  /** Slide-over heading. "{market}" is replaced with the market label. */
  guideHeading: string
  /** aria-label of the slide-over close button. */
  closeLabel: string
  /** Alt text for the primary org's logo. "{org}" is replaced with the org name. */
  logoAlt: string
  /**
   * Market chip label and reporting channels. `null` means "use the shared
   * market data in lib/emergency-contacts" (US and the other English faces).
   * The Qatar face has no entry there, so QA/ar carry the channels inline.
   */
  marketLabel: string | null
  channels: ReportChannel[] | null
}

const qaChannels: ReportChannel[] = [
  {
    org: 'Ministry of Interior',
    detail: 'Hotline 2347444',
    whenToUse:
      'The MOI cybercrime unit. Start here for any scam, whether or not you lost money. You can also report in the Metrash2 app.',
    links: [
      { text: '2347444', href: 'tel:+9742347444', kind: 'phone' },
      { text: 'WhatsApp 66815757', href: 'https://wa.me/97466815757', kind: 'url' },
      { text: 'cccc@moi.gov.qa', href: 'mailto:cccc@moi.gov.qa', kind: 'url' },
    ],
  },
  {
    org: 'Your bank’s fraud line',
    detail: 'The number on the back of your card',
    whenToUse: 'If money moved, or if your card or account details were shared.',
    links: [],
  },
  {
    org: 'Qatar Central Bank',
    detail: 'qcb.gov.qa',
    whenToUse:
      'If your bank does not resolve a dispute about a scam payment, you can escalate it to the QCB.',
    links: [{ text: 'qcb.gov.qa', href: 'https://www.qcb.gov.qa', kind: 'url' }],
  },
]

export const copy: MarketVariants<Slide7ReportCopy> = {
  // US is canonical. GB / CA / AU below override only the strings that
  // genuinely differ. Spelling variants (organize/organise, etc.) are
  // handled automatically by useSlideCopy via toMarketSpelling.
  US: {
    eyebrow: 'Step five',
    title: 'Report it.',
    body: "Reporting helps investigators catch scammers and warn others. Even if you didn’t lose money, your report still matters.",
    startHereLabel: 'Start here',
    buttonLabel: 'Reporting guide',
    closingLine:
      "Also report it on the platform where the scam happened. They can suspend the scammer’s account and stop them from targeting others.",
    introLine:
      "You don’t need to know who scammed you, or have all the answers. A short, honest report is enough.",
    haveTheseHeading: 'Have this info ready',
    have1: 'How they contacted you and what they asked for',
    have2: 'Phone numbers, emails, usernames, or links they used',
    have3: 'When it happened, and any amount lost',
    have4: 'Any photos, screenshots, or receipts you have',
    whereToReportPrefix: 'Where to report in the',
    policeNote:
      'If a scam is in progress, or if you ever feel unsafe, call 911 first. The agencies below are for reporting after the fact.',
    whatHappensNextHeading: 'What happens next',
    whatHappensNextBody:
      "Most agencies don’t investigate every report individually. Your report joins a pool of intelligence that helps shut scammers down and warn other people. You may not hear back, and that’s normal.",
    documentationLine:
      "For insurance claims, tax matters, or seeking reimbursement from your bank, you may need a report reference number.",
    guideHeading: 'Reporting in the {market}',
    closeLabel: 'Close',
    logoAlt: '{org} logo',
    marketLabel: null,
    channels: null,
  },
  GB: {
    policeNote:
      'If a scam is in progress, or if you ever feel unsafe, call 999 first. The agencies below are for reporting after the fact.',
  },
  CA: {
    policeNote:
      'If a scam is in progress, or if you ever feel unsafe, call 911 first. The agencies below are for reporting after the fact.',
  },
  AU: {
    policeNote:
      'If a scam is in progress, or if you ever feel unsafe, call 000 first. The agencies below are for reporting after the fact.',
  },
  QA: {
    policeNote:
      'If a scam is in progress, or if you ever feel unsafe, call 999 first. The agencies below are for reporting after the fact.',
    // Qatar has no personal income tax, so "tax matters" is dropped.
    documentationLine:
      'For insurance claims or seeking reimbursement from your bank, you may need a report reference number.',
    marketLabel: 'Qatar',
    whereToReportPrefix: 'Where to report in',
    guideHeading: 'Reporting in {market}',
    channels: qaChannels,
  },
  ar: {
    eyebrow: 'الخطوة الخامسة',
    title: 'أبلغ عن الاحتيال.',
    body: 'الإبلاغ يساعد المحققين على الإمساك بالمحتالين وتحذير الآخرين. حتى لو لم تخسر مالاً، فبلاغك مهم.',
    startHereLabel: 'ابدأ من هنا',
    buttonLabel: 'دليل الإبلاغ',
    closingLine:
      'أبلغ أيضاً عن الاحتيال على المنصة التي حدث فيها. يمكنهم تعليق حساب المحتال ومنعه من استهداف الآخرين.',
    introLine:
      'لا تحتاج إلى معرفة من احتال عليك، ولا إلى امتلاك كل الإجابات. بلاغ قصير وصادق يكفي.',
    haveTheseHeading: 'جهّز هذه المعلومات',
    have1: 'كيف تواصلوا معك وماذا طلبوا',
    have2: 'أرقام الهواتف أو عناوين البريد الإلكتروني أو أسماء المستخدمين أو الروابط التي استخدموها',
    have3: 'متى حدث ذلك، وأي مبلغ خسرته',
    have4: 'أي صور أو لقطات شاشة أو إيصالات لديك',
    whereToReportPrefix: 'جهات الإبلاغ في',
    policeNote:
      'إذا كان الاحتيال جارياً الآن، أو إذا شعرت في أي وقت بأنك في خطر، فاتصل بالرقم 999 أولاً. الجهات أدناه مخصصة للإبلاغ بعد وقوع الحادثة.',
    whatHappensNextHeading: 'ماذا يحدث بعد ذلك',
    whatHappensNextBody:
      'لا تحقق معظم الجهات في كل بلاغ على حدة. ينضم بلاغك إلى مجموعة من المعلومات التي تساعد على إيقاف المحتالين وتحذير الآخرين. قد لا يصلك ردّ، وهذا أمر طبيعي.',
    documentationLine:
      'قد تحتاج إلى الرقم المرجعي للبلاغ عند تقديم مطالبة تأمين أو طلب تعويض من بنكك.',
    guideHeading: 'الإبلاغ في {market}',
    closeLabel: 'إغلاق',
    logoAlt: 'شعار {org}',
    marketLabel: 'قطر',
    channels: [
      {
        org: 'وزارة الداخلية',
        detail: 'الخط الساخن 2347444',
        whenToUse:
          'وحدة مكافحة الجرائم الإلكترونية في وزارة الداخلية. ابدأ من هنا في أي احتيال، سواء خسرت مالاً أم لا. يمكنك الإبلاغ أيضاً عبر تطبيق مطراش2.',
        links: [
          { text: '2347444', href: 'tel:+9742347444', kind: 'phone' },
          { text: 'واتساب 66815757', href: 'https://wa.me/97466815757', kind: 'url' },
          { text: 'cccc@moi.gov.qa', href: 'mailto:cccc@moi.gov.qa', kind: 'url' },
        ],
      },
      {
        org: 'خط الاحتيال في بنكك',
        detail: 'الرقم المدوّن على ظهر بطاقتك',
        whenToUse: 'إذا خرجت أموال من حسابك، أو إذا شاركت بيانات بطاقتك أو حسابك.',
        links: [],
      },
      {
        org: 'مصرف قطر المركزي',
        detail: 'qcb.gov.qa',
        whenToUse:
          'إذا لم يحلّ بنكك نزاعاً بشأن دفعة احتيالية، يمكنك تصعيد الأمر إلى مصرف قطر المركزي.',
        links: [{ text: 'qcb.gov.qa', href: 'https://www.qcb.gov.qa', kind: 'url' }],
      },
    ],
  },
}
